import { requestBlob, requestJson } from "services/http";

const jsonOptions = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export const getCameraRuns = (mac) =>
  requestJson(`/api/padihat/devices/${encodeURIComponent(mac)}/camera-runs/`);

export const createCameraRun = (mac, runId, intervalMinutes) =>
  requestJson(
    `/api/padihat/devices/${encodeURIComponent(mac)}/camera-runs/`,
    jsonOptions("POST", {
      run_id: runId,
      interval_sec: intervalMinutes * 60,
    })
  );

// Display-only per-camera setting shared by every viewer: { imageRotationDeg: 0 | 180 }.
export const updateCameraSettings = (mac, { imageRotationDeg }) =>
  requestJson(
    `/api/padihat/devices/${encodeURIComponent(mac)}/camera-settings/`,
    jsonOptions("PATCH", { image_rotation_deg: imageRotationDeg })
  );

export const getCameraStatus = (mac) =>
  requestJson(`/api/padihat/devices/${encodeURIComponent(mac)}/camera-status/`);

export const getCameraRun = (runId, cursor = "", limit = 48) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return requestJson(`/api/padihat/camera-runs/${runId}/?${params.toString()}`);
};

export const getCameraMissing = (runId) =>
  requestJson(`/api/padihat/camera-runs/${runId}/missing/`);

export const deleteCameraRun = (runId) =>
  requestJson(`/api/padihat/camera-runs/${runId}/`, { method: "DELETE" });

export const stopCameraRun = (runId) =>
  requestJson(`/api/padihat/camera-runs/${runId}/stop/`, { method: "POST" });

// "Capture now" (Camera tab) - a standalone always-on camera notices it within
// a few seconds (it polls active-camera-run continuously); a PadiHAT-relayed
// MQTT camera has no command channel to notice it at all. The pump-finished
// hook in padihat-backend requests captures through the same mechanism. See
// padihat-backend's camera_control.request_capture/active_camera_run/
// camera_upload for the other end of this.
export const requestTestCapture = (mac) =>
  requestJson(`/api/padihat/devices/${encodeURIComponent(mac)}/request-test-capture/`, {
    method: "POST",
  });

export const getProtectedImageBlob = (url, options = {}) => requestBlob(url, options);

const safeFilename = (value) =>
  (value || "camera-export.zip").replace(/[^A-Za-z0-9._-]/g, "_");

export async function downloadCameraRun(run) {
  const { blob, contentDisposition } = await requestBlob(
    `/api/padihat/camera-runs/${run.id}/export/`
  );
  const encoded = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = contentDisposition.match(/filename="?([^";]+)"?/i);
  const filename = safeFilename(
    encoded ? decodeURIComponent(encoded[1]) : plain ? plain[1] : `${run.run_id}.zip`
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// Ground truth: record the real water level (cm) a person read off a photo; null clears it.
// Resolves to the updated image payload.
export const labelCameraImage = (imageId, trueLevelCm) =>
  requestJson(`/api/padihat/camera-images/${imageId}/label/`, jsonOptions("PATCH", { true_level_cm: trueLevelCm })).then(
    (result) => result.image
  );
